import React from "react";
import { DesignTeaser } from "@/shared/types/popup-design";
import { fontStack } from "./engine";

// WhatsApp icon (same path as the popup's)
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={{ width: 16, height: 16, flexShrink: 0 }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z" />
  </svg>
);

interface TeaserTabProps {
  teaser: DesignTeaser;
  fontFamily: string;
  onClick: () => void;
}

/**
 * The edge-of-screen teaser card ("Get R100 off"). Fixed to a screen edge;
 * vertical text on left/right, horizontal pill in the corners. Clicking it
 * opens (or re-opens) the popup.
 */
export const TeaserTab: React.FC<TeaserTabProps> = ({ teaser, fontFamily, onClick }) => {
  if (!teaser.enabled || !teaser.text) return null;

  const isVertical = teaser.orientation === "vertical";
  const isEdge = teaser.position === "left" || teaser.position === "right";

  const positionStyles: React.CSSProperties = (() => {
    switch (teaser.position) {
      case "left":
        return { left: 0, top: "50%", transform: "translateY(-50%)" };
      case "right":
        return { right: 0, top: "50%", transform: "translateY(-50%)" };
      case "bottom_left":
        return { left: "20px", bottom: "20px" };
      case "bottom_right":
      default:
        return { right: "20px", bottom: "20px" };
    }
  })();

  // Edge tabs hug the screen edge: round only the inward corners.
  const radius = teaser.radius || "12px";
  const borderRadius = isEdge
    ? teaser.position === "left"
      ? `0 ${radius} ${radius} 0`
      : `${radius} 0 0 ${radius}`
    : radius;

  const animationName =
    teaser.position === "left"
      ? "flowapt-slide-in-left"
      : isEdge
        ? "flowapt-slide-in-right"
        : "flowapt-slide-up";

  const verticalEdge = isVertical && isEdge;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={teaser.text}
      className="flowapt-animated"
      style={{
        position: "fixed",
        zIndex: 2147483000,
        // The widget host sits at pointer-events:none whenever no blocking
        // popup is open — the teaser must re-enable events for itself or
        // clicks fall through to the page.
        pointerEvents: "auto",
        ...positionStyles,
        display: "flex",
        // vertical edge tabs stack the (upright) icon above the vertical text
        flexDirection: verticalEdge ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: verticalEdge ? "14px 10px" : "12px 18px",
        backgroundColor: teaser.background,
        color: teaser.textColor,
        borderRadius,
        border: "none",
        cursor: "pointer",
        fontFamily: fontStack(fontFamily),
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: verticalEdge ? "0.04em" : undefined,
        // Never taller than the screen (edge tab) or wider than it (corner pill).
        maxHeight: verticalEdge ? "calc(100vh - 32px)" : undefined,
        maxWidth: verticalEdge ? undefined : "calc(100vw - 40px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
        animation: `${animationName} 300ms ease-out both`,
        transition: "filter 150ms ease, transform 150ms ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.filter = "brightness(1.08)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.filter = "";
      }}
    >
      {teaser.showIcon && (
        <WhatsAppIcon className="" />
      )}
      <span
        style={
          verticalEdge
            ? {
                // One column, always. On Lean Living's Shopify theme preview
                // (iOS Safari, 30 Sep 2026) "10% OFF" rendered as two columns;
                // the break was not reproduced in desktop WebKit or Chromium, so
                // the rule is a hard no-wrap rather than an engine workaround.
                // Text longer than the cap is cut, never wrapped. No
                // text-overflow ellipsis here: in vertical text WebKit counts
                // the trailing letter-spacing as overflow and replaces the
                // last glyph ("10% OF.", live on 30 Sep 2026).
                writingMode: "vertical-rl" as const,
                whiteSpace: "nowrap",
                maxHeight: "min(70vh, 420px)",
                overflow: "hidden",
              }
            : {
                // Corner pill: at most two lines, then an ellipsis.
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical" as const,
                overflow: "hidden",
                maxWidth: "min(60vw, 280px)",
                textAlign: "left" as const,
                overflowWrap: "anywhere" as const,
              }
        }
      >
        {teaser.text}
      </span>
    </button>
  );
};

export default TeaserTab;
